import React, { useEffect, useMemo, useState } from 'react';
import Plot from 'react-plotly.js';
import { FiBarChart2 } from 'react-icons/fi';
import { toHistoryKey, getLatestHistoryEntry } from './useSensorDataTab.utils';
import S from './SensorDataTab.module.scss';

const PALETTE = [
  '#a6163f', '#2980b9', '#27ae60', '#f39c12',
  '#8e44ad', '#16a085', '#c0392b', '#1a5276',
];

const toISO = (timeUnix) => new Date(timeUnix * 1000).toISOString();

// The chart plots the calculated values (fallback to raw value if absent).
const chartValue = (r) => r.calc_value ?? r.value;
const chartOldValue = (r) => r.old_calc_value ?? r.old_value;

const SensorChartTab = ({
  rows,
  historyMap,
  getSensorNameById,
  showChart = true,
  onToggleChart,
  sensorKeys = [],
  chartSensorKey,
  onChartSensorKeyChange,
  getPositionPrefix,
}) => {
  const [showOriginal, setShowOriginal] = useState(false);

  const traces = useMemo(() => {
    if (!rows.length) return [];

    const groups = {};
    rows.forEach((row) => {
      const key = `${row._sensorId}_${row.position_in_sensor}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });

    let colorIdx = 0;
    const allTraces = [];

    Object.entries(groups).forEach(([key, groupRows]) => {
      const sorted = [...groupRows].sort((a, b) => a.time_unix - b.time_unix);
      const [sId, pos] = key.split('_');
      const sensorName = getSensorNameById(Number(sId));
      const label = `${sensorName} P${pos}`;
      const prefix = getPositionPrefix
        ? getPositionPrefix(Number(sId), Number(pos))
        : '';
      const valueLabel = prefix || 'Valeur';
      const color = PALETTE[colorIdx % PALETTE.length];
      colorIdx++;

      const deletedRows = sorted.filter((r) => {
        const histKey = toHistoryKey(r._sensorId, r.pk_test);
        const entries = historyMap[histKey] ?? [];
        return getLatestHistoryEntry(entries)?.update_type === 'Delete';
      });
      const deletedIdSet = new Set(deletedRows.map((r) => r.id));

      const activeRows = sorted.filter((r) => !deletedIdSet.has(r.id));
      const dirtyRows = activeRows.filter((r) => r._dirty);

      // Main line trace — all active rows (line stays continuous over modified points)
      if (activeRows.length) {
        allTraces.push({
          x: activeRows.map((r) => toISO(r.time_unix)),
          y: activeRows.map(chartValue),
          type: 'scatter',
          mode: 'lines+markers',
          name: label,
          line: { color, width: 2 },
          marker: { size: 7, color },
          hovertemplate: `<b>${label}</b><br>Date : %{x}<br>${valueLabel} : %{y}<extra></extra>`,
        });
      }

      // Red ring around modified points (circle-open overlay)
      if (dirtyRows.length) {
        allTraces.push({
          x: dirtyRows.map((r) => toISO(r.time_unix)),
          y: dirtyRows.map(chartValue),
          customdata: dirtyRows.map((r) => chartOldValue(r) ?? '—'),
          type: 'scatter',
          mode: 'markers',
          name: `${label} — modifié`,
          marker: {
            size: 18,
            color: 'rgba(0,0,0,0)',
            symbol: 'circle-open',
            line: { color: '#e74c3c', width: 2.5 },
          },
          hovertemplate:
            `<b>${label} — Modifié</b><br>` +
            `Date : %{x}<br>` +
            `Nouvelle valeur : %{y}<br>` +
            `Ancienne valeur : %{customdata}<extra></extra>`,
        });
      }

      // Gray open circle at the original position before edit
      const dirtyWithOld = dirtyRows.filter((r) => chartOldValue(r) != null);
      if (dirtyWithOld.length) {
        allTraces.push({
          x: dirtyWithOld.map((r) => toISO(r.old_time_unix ?? r.time_unix)),
          y: dirtyWithOld.map((r) => chartOldValue(r)),
          type: 'scatter',
          mode: 'markers',
          name: `${label} — avant modif.`,
          marker: {
            size: 9,
            color: 'rgba(0,0,0,0)',
            symbol: 'circle-open',
            line: { color: '#aaaaaa', width: 2 },
          },
          hovertemplate: `<b>${label} — Avant modif.</b><br>Date : %{x}<br>Ancienne valeur : %{y}<extra></extra>`,
        });
      }

      // Deleted rows — red X
      if (deletedRows.length) {
        allTraces.push({
          x: deletedRows.map((r) => toISO(r.time_unix)),
          y: deletedRows.map(chartValue),
          type: 'scatter',
          mode: 'markers',
          name: `${label} — supprimé`,
          marker: {
            size: 13,
            color: '#e74c3c',
            symbol: 'x',
            line: { color: '#e74c3c', width: 2.5 },
          },
          hovertemplate: `<b>${label} — Supprimé</b><br>Date : %{x}<br>${valueLabel} : %{y}<extra></extra>`,
        });
      }

      // Original curve overlay (before modifications) — dotted, semi-transparent.
      // Uses pre-edit values for modified points and includes deleted points,
      // since they were part of the original series.
      if (showOriginal) {
        const originalPts = sorted
          .map((r) => {
            const oTime = r._dirty ? (r.old_time_unix ?? r.time_unix) : r.time_unix;
            const oVal = r._dirty ? (chartOldValue(r) ?? chartValue(r)) : chartValue(r);
            return { oTime: Number(oTime), oVal };
          })
          .filter((p) => p.oVal != null && Number.isFinite(p.oTime))
          .sort((a, b) => a.oTime - b.oTime);

        if (originalPts.length) {
          allTraces.push({
            x: originalPts.map((p) => toISO(p.oTime)),
            y: originalPts.map((p) => p.oVal),
            type: 'scatter',
            mode: 'lines+markers',
            name: `${label} — original`,
            line: { color, width: 1.5, dash: 'dot' },
            marker: { size: 5, color },
            opacity: 0.4,
            hovertemplate: `<b>${label} — Courbe originale</b><br>Date : %{x}<br>${valueLabel} : %{y}<extra></extra>`,
          });
        }
      }
    });

    return allTraces;
  }, [rows, historyMap, getSensorNameById, getPositionPrefix, showOriginal]);

  // The original curve only differs when something was modified (unsaved edits)
  // or deleted. With nothing modified, the toggle is pointless → disable it.
  const hasModifications = useMemo(
    () =>
      rows.some((r) => {
        if (r._dirty) return true;
        const entries = historyMap[toHistoryKey(r._sensorId, r.pk_test)] ?? [];
        return getLatestHistoryEntry(entries)?.update_type === 'Delete';
      }),
    [rows, historyMap]
  );

  useEffect(() => {
    if (!hasModifications && showOriginal) setShowOriginal(false);
  }, [hasModifications, showOriginal]);

  return (
    <div className={S.chartInlineWrapper}>
      <div className={S.chartInlineHeader}>
        <span className={S.chartInlineTitle}>Visualisation des modifications</span>

        <button
          type="button"
          role="switch"
          aria-checked={showOriginal}
          disabled={!hasModifications}
          className={`${S.originalToggle} ${showOriginal ? S.originalToggleOn : ''} ${!hasModifications ? S.originalToggleDisabled : ''
            }`}
          onClick={() => setShowOriginal((v) => !v)}
          title={
            !hasModifications
              ? 'Aucune modification à comparer'
              : showOriginal
                ? 'Masquer la courbe originale'
                : 'Afficher la courbe originale (avant modifications)'
          }
        >
          <span className={S.originalToggleTrack}>
            <span className={S.originalToggleThumb} />
          </span>
          Courbe originale
        </button>

        <button
          type="button"
          className={`${S.chartVisualiserBtn} ${showChart ? S.chartVisualiserBtnActive : ''
            }`}
          onClick={onToggleChart}
          title={showChart ? 'Masquer le graphique' : 'Afficher le graphique'}
        >
          <FiBarChart2 size={15} />
          Visualiser
        </button>

        {sensorKeys.length > 1 && (
          <select
            className={S.chartHeaderSelect}
            value={chartSensorKey}
            onChange={(e) => onChartSensorKeyChange?.(e.target.value)}
          >
            {sensorKeys.map((sk) => (
              <option key={sk.key} value={sk.key}>
                {sk.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {showChart && (
        <div className={S.chartPlotArea}>
          <Plot
            data={traces}
            layout={{
              autosize: true,
              margin: { t: 10, r: 20, b: 70, l: 55 },
              paper_bgcolor: '#ffffff',
              plot_bgcolor: '#fafafa',
              xaxis: {
                title: 'Date',
                type: 'date',
                tickformat: '%d/%m %H:%M',
                hoverformat: '%d/%m/%Y %H:%M',
                gridcolor: '#e0e0e0',
                linecolor: '#cccccc',
                zeroline: false,
              },
              yaxis: {
                title: 'Valeur calculée',
                gridcolor: '#e0e0e0',
                linecolor: '#cccccc',
                zeroline: false,
              },
              legend: {
                orientation: 'h',
                y: -0.28,
                x: 0,
                font: { size: 10 },
              },
              hovermode: 'closest',
            }}
            config={{
              responsive: true,
              displayModeBar: true,
              scrollZoom: false,
              displaylogo: false,
              modeBarButtonsToRemove: ['lasso2d', 'select2d', 'autoScale2d', 'toggleSpikelines', 'hoverClosestCartesian', 'hoverCompareCartesian', 'zoom2d',  'pan2d', 'resetScale2d', 'zoomIn2d', 'zoomOut2d'],
            }}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler
          />
        </div>
      )}
    </div>
  );
};

export default SensorChartTab;
